import { useCallback, useEffect, useState } from 'react';
import { appComponentsStorage } from '../../storage';
import { CUSTOMIZED_SHORTCUTS_STORAGE_KEY } from '../consts';
import type { CommandKeys } from '../types/CommandKeys';

export type CustomizedShortcuts = Record<string, CommandKeys[]>;

const readFromStorage = (): CustomizedShortcuts => {
  return (
    (appComponentsStorage.get(CUSTOMIZED_SHORTCUTS_STORAGE_KEY) as
      | CustomizedShortcuts
      | undefined) ?? {}
  );
};

const writeToStorage = (next: CustomizedShortcuts) => {
  appComponentsStorage.set(CUSTOMIZED_SHORTCUTS_STORAGE_KEY, next);
  // Notify same-tab consumers — `window.storage` only fires for cross-tab writes.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new StorageEvent('storage', { key: CUSTOMIZED_SHORTCUTS_STORAGE_KEY })
    );
  }
};

/**
 * Manages the local-storage-backed map of customized keyboard shortcuts.
 *
 * Returns the current map plus writers that persist changes and fan out a
 * synthetic `storage` event so other in-tab listeners (e.g. the
 * `CommandRegistry`) re-derive their state.
 */
export function useCustomShortcutsStore() {
  const [customizedShortcuts, setCustomizedShortcuts] =
    useState<CustomizedShortcuts>(readFromStorage);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === CUSTOMIZED_SHORTCUTS_STORAGE_KEY) {
        setCustomizedShortcuts(readFromStorage());
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setShortcut = useCallback(
    (id: string, keys: ReadonlyArray<CommandKeys>) => {
      setCustomizedShortcuts((prev) => {
        const next: CustomizedShortcuts = { ...prev, [id]: [...keys] };
        writeToStorage(next);
        return next;
      });
    },
    []
  );

  const resetShortcut = useCallback((id: string) => {
    setCustomizedShortcuts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      writeToStorage(next);
      return next;
    });
  }, []);

  const resetMany = useCallback((ids: ReadonlyArray<string>) => {
    setCustomizedShortcuts((prev) => {
      const toRemove = ids.filter((id) => id in prev);
      if (toRemove.length === 0) return prev;
      const next = { ...prev };
      toRemove.forEach((id) => delete next[id]);
      writeToStorage(next);
      return next;
    });
  }, []);

  return {
    customizedShortcuts,
    setShortcut,
    resetShortcut,
    resetMany,
  };
}
